import * as THREE from 'three';
import { pixelRatio2d } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { FurnitureAssets } from '../furniture/furnitureAssets';

/** 家具的 2d 几何体来自 SVG，与画布上的 InstancedMesh 共享同一份缓存，所以观感天然一致 */
export const getFurnitureAsset = (svgContent: string) => FurnitureAssets.getAsset(svgContent);

export interface IFurnitureTransform {
  center: IPoint2d;
  rotation: number;
  width: number;
  thickness: number;
  /** 家具之间的遮挡排序，画布上按放置顺序递增 */
  zOffset?: number;
}

const _offsetMatrix = new THREE.Matrix4();
const _flipMatrix = new THREE.Matrix4().makeRotationX(Math.PI);
const _scaleMatrix = new THREE.Matrix4();
const _rotationMatrix = new THREE.Matrix4();
const _positionMatrix = new THREE.Matrix4();

/**
 * 与 FurnitureInstanceManager.computeMatrix 相同的合成顺序：position · rotation · scale · flip · offset。
 * 画布上的家具可以直接拿现成的实例矩阵，没有实例可拿时（比如布局结果）用这个算。
 */
export const buildFurnitureMatrix = (
  transform: IFurnitureTransform,
  asset: { originWidth: number; originHeight: number; offsetX: number; offsetY: number }
) => {
  const { center, rotation, width, thickness, zOffset = 0 } = transform;

  _offsetMatrix.makeTranslation(asset.offsetX, -asset.offsetY, 0);
  _scaleMatrix.makeScale(width / asset.originWidth / pixelRatio2d, thickness / asset.originHeight / pixelRatio2d, 1);
  _rotationMatrix.makeRotationZ(rotation);
  _positionMatrix.makeTranslation(center.x / pixelRatio2d, center.y / pixelRatio2d, zOffset);

  return new THREE.Matrix4()
    .copy(_positionMatrix)
    .multiply(_rotationMatrix)
    .multiply(_scaleMatrix)
    .multiply(_flipMatrix)
    .multiply(_offsetMatrix);
};

/**
 * 几何体与材质都取自 FurnitureAssets 的全局缓存，标记成 shared，销毁场景时要跳过，
 * 否则会把画布还在用的资源一起销毁掉。
 */
export const createFurnitureMesh = (svgContent: string, matrix: THREE.Matrix4): THREE.Mesh | undefined => {
  const asset = getFurnitureAsset(svgContent);
  if (!asset.mergedGeometry) return undefined;

  const mesh = new THREE.Mesh(asset.mergedGeometry, FurnitureAssets.instancedMaterials);
  mesh.userData.sharedGeometry = true;
  mesh.userData.sharedMaterial = true;
  mesh.matrixAutoUpdate = false;
  mesh.matrix.copy(matrix);

  return mesh;
};
